import type { GameConfig, Language, Phase, Plan, Role } from './types.ts';

/**
 * The Tutorial (GDD §11): five heists on the bank, each teaching one role. A chapter is a game
 * on a fixed seed whose first puzzles are the ones that role works, against bots that play it
 * at the easy level. When the player is not the mastermind, the bot mastermind proposes the
 * scripted `plan` instead of choosing its own, so the lesson starts within a minute.
 *
 * Seeds and plans were picked by measurement: `pnpm exec tsx tools/sim/tutorial.ts check`
 * plays every chapter with bots and fails when one stops teaching what it should; `search`
 * finds new ones after a change to the puzzle generators.
 */

export interface TutorialChapter {
  id: 1 | 2 | 3 | 4 | 5;
  /** The player's role. */
  role: Role;
  /** Players at the table, the human included. */
  players: 3 | 4 | 5;
  seed: string;
  /**
   * The plan the lesson is built on. The bot mastermind proposes it; when the player is the
   * mastermind (chapter 3) it is the suggested plan, shown as an example.
   */
  plan: Plan;
}

const plan = (entrance: Plan['entrance'], route: string[]): Plan => ({
  entrance,
  route: route.map((r) => `bank.${r}`),
  detours: [],
  gear: [],
});

export const TUTORIAL_CHAPTERS: readonly TutorialChapter[] = [
  {
    id: 1,
    role: 'locksmith',
    players: 3,
    seed: 'tutorial-1-29',
    plan: plan('service', ['serviceYard', 'atrium', 'tellers', 'vault']),
  },
  {
    id: 2,
    role: 'hacker',
    players: 3,
    seed: 'tutorial-2-51',
    plan: plan('service', ['serviceYard', 'atrium', 'tellers', 'vault']),
  },
  {
    id: 3,
    role: 'mastermind',
    players: 3,
    seed: 'tutorial-3-51',
    plan: plan('service', ['serviceYard', 'atrium', 'tellers', 'vault']),
  },
  {
    id: 4,
    role: 'lookout',
    players: 4,
    seed: 'tutorial-4-3',
    plan: plan('roof', ['skylight', 'gallery', 'atrium', 'tellers', 'vault']),
  },
  {
    id: 5,
    role: 'driver',
    players: 5,
    seed: 'tutorial-5-32',
    plan: plan('sewer', ['sewerTunnel', 'powerRoom', 'armoredCorridor', 'vault']),
  },
];

const ROLE_ORDER: Record<3 | 4 | 5, readonly Role[]> = {
  3: ['mastermind', 'locksmith', 'hacker'],
  4: ['mastermind', 'locksmith', 'hacker', 'lookout'],
  5: ['mastermind', 'locksmith', 'hacker', 'lookout', 'driver'],
};

export interface TutorialConfigOptions {
  seed?: string;
  /** Names of the seats, the player's first. */
  names?: readonly string[];
  language?: Language;
}

/** The game a chapter is played on: the player in seat 1, everybody else a bot. */
export function tutorialConfig(
  chapter: TutorialChapter,
  options: TutorialConfigOptions = {},
): GameConfig {
  const players = Array.from({ length: chapter.players }, (_, i) => ({
    id: `p${i + 1}`,
    name: options.names?.[i] ?? `p${i + 1}`,
  }));
  const rest = ROLE_ORDER[chapter.players].filter((r) => r !== chapter.role);
  return {
    players,
    mapId: 'bank',
    difficulty: 'easy',
    infiltrator: 'never',
    language: options.language ?? 'en',
    roles: Object.fromEntries(
      players.map((p, i): [string, Role] => [p.id, i === 0 ? chapter.role : (rest[i - 1] as Role)]),
    ),
  };
}

/** The events a lesson looks at: a client that keeps only these has all it needs. */
export const LESSON_EVENT_TYPES: readonly string[] = [
  'puzzle.solved',
  'plan.approved',
  'corridor.cleared',
  'escape.done',
];

export interface LessonEvent {
  type: string;
  data: Record<string, unknown>;
}

/** Whether the chapter has taught what it set out to, given everything seen so far. */
export function lessonDone(
  chapter: TutorialChapter['id'],
  seen: readonly LessonEvent[],
  phase: Phase,
): boolean {
  const solved = new Set(seen.filter((e) => e.type === 'puzzle.solved').map((e) => e.data['type']));
  const had = (type: string): boolean => seen.some((e) => e.type === type);
  switch (chapter) {
    case 1:
      return solved.has('lock');
    case 2:
      return solved.has('panel') && solved.has('cameras');
    case 3:
      return had('plan.approved') && solved.has('safe');
    case 4:
      return had('corridor.cleared');
    case 5:
      return had('escape.done') || phase === 'hideout' || phase === 'reveal' || phase === 'ended';
  }
}
